import React, { useRef, useState } from "react";
import { toast } from "sonner";

/**
 * BulkUploader — drop multiple files at once, upload sequentially.
 *
 * Built specifically to make re-loading a backlog of price lists or
 * transaction files painless. Each file becomes a row in a per-file table
 * with its own metadata inputs (e.g. effective_date for price lists,
 * customer_id for transactions); fields auto-fill from filename hints
 * where possible (e.g. "esso_2026-04-28.csv" → network=esso, date=2026-04-28).
 *
 * Props:
 *   open: bool — controls the modal visibility
 *   onClose: () => void
 *   onAllDone: () => void — fired after the queue runs to completion
 *   title: string — modal heading
 *   accept: string — file input `accept` attribute
 *   defaultMeta: object — initial per-file metadata (e.g. {network: "esso"})
 *   metaFields: [{key, label, type, options?, render?}] — per-file editable fields
 *   detectMeta: (file) => object — populated metadata based on filename
 *   uploadOne: (file, meta) => Promise<{summary: string}> — does the actual POST
 *
 * Each row shows: filename · per-meta inputs · status badge (pending/uploading/done/error) · summary
 */
export default function BulkUploader({
  open,
  onClose,
  onAllDone,
  title,
  accept,
  defaultMeta,
  metaFields,
  detectMeta,
  uploadOne,
}) {
  // queue: [{ id, file, meta, status, summary, error }]
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  if (!open) return null;

  const addFiles = (filelist) => {
    const next = [];
    for (const f of filelist) {
      const detected = (detectMeta && detectMeta(f)) || {};
      next.push({
        id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        meta: { ...defaultMeta, ...detected },
        status: "pending",
        summary: "",
        error: "",
      });
    }
    setQueue((q) => [...q, ...next]);
  };

  const updateMeta = (id, patch) => {
    setQueue((q) =>
      q.map((row) => (row.id === id ? { ...row, meta: { ...row.meta, ...patch } } : row))
    );
  };

  const remove = (id) => setQueue((q) => q.filter((r) => r.id !== id));

  const runQueue = async () => {
    if (queue.length === 0) return;
    setBusy(true);
    let ok = 0, fail = 0;
    // Sequential, not Promise.all, because each upload mutates state and we
    // want the user to see live progress on slow files.
    for (const row of queue) {
      if (row.status === "done") continue;
      setQueue((q) => q.map((r) => (r.id === row.id ? { ...r, status: "uploading", error: "" } : r)));
      try {
        const res = await uploadOne(row.file, row.meta);
        setQueue((q) =>
          q.map((r) => (r.id === row.id ? { ...r, status: "done", summary: res?.summary || "OK" } : r))
        );
        ok += 1;
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || "Upload failed";
        setQueue((q) => q.map((r) => (r.id === row.id ? { ...r, status: "error", error: msg } : r)));
        fail += 1;
      }
    }
    setBusy(false);
    if (ok > 0) toast.success(`Uploaded ${ok} of ${queue.length}${fail ? ` · ${fail} failed` : ""}`);
    else if (fail > 0) toast.error(`All ${fail} uploads failed`);
    if (ok > 0) onAllDone?.();
  };

  const reset = () => {
    setQueue([]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const allDone = queue.length > 0 && queue.every((r) => r.status === "done");
  const close = () => { if (busy) return; reset(); onClose(); };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={close}
      data-testid="bulk-uploader-overlay"
    >
      <div
        className="bg-white w-full max-w-5xl border border-zinc-200 max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="bulk-uploader-modal"
      >
        <div className="gf-card-header">
          <h3>{title}</h3>
          <span className="text-xs text-zinc-500 font-mono">
            {queue.length} FILE{queue.length === 1 ? "" : "S"} · {queue.filter((r) => r.status === "done").length} DONE
          </span>
        </div>

        <div className="flex-1 overflow-auto p-4 space-y-3">
          <div className="border-2 border-dashed border-zinc-300 p-6 text-center bg-zinc-50">
            <input
              ref={fileRef}
              type="file"
              multiple
              accept={accept}
              className="hidden"
              data-testid="bulk-file-input"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) addFiles(files);
                if (fileRef.current) fileRef.current.value = ""; // allow re-selecting same files
              }}
            />
            <button
              type="button"
              className="gf-btn gf-btn-primary"
              onClick={() => fileRef.current?.click()}
              data-testid="bulk-pick-files"
            >
              Choose files
            </button>
            <div className="text-xs text-zinc-500 mt-2 font-mono">
              {accept.replace(/\./g, "").toUpperCase()} · drop multiple files · upload runs sequentially
            </div>
          </div>

          {queue.length > 0 && (
            <div className="overflow-auto border border-zinc-200">
              <table className="gf-table">
                <thead style={{ position: "sticky", top: 0, background: "white" }}>
                  <tr>
                    <th>FILE</th>
                    {metaFields.map((f) => <th key={f.key}>{f.label}</th>)}
                    <th>STATUS</th>
                    <th>NOTES</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {queue.map((row) => {
                    const isLocked = row.status === "uploading" || row.status === "done";
                    return (
                      <tr key={row.id} data-testid={`bulk-row-${row.id}`}>
                        <td className="text-xs">
                          <div className="font-mono">{row.file.name}</div>
                          <div className="text-zinc-400">{Math.round(row.file.size / 1024)} KB</div>
                        </td>
                        {metaFields.map((f) => (
                          <td key={f.key}>
                            {f.render ? (
                              f.render(row.meta, (patch) => updateMeta(row.id, patch), isLocked)
                            ) : f.type === "select" ? (
                              <select
                                className="gf-input"
                                value={row.meta[f.key] ?? ""}
                                disabled={isLocked}
                                onChange={(e) => updateMeta(row.id, { [f.key]: e.target.value })}
                                data-testid={`bulk-${f.key}-${row.id}`}
                                style={{ minWidth: 110 }}
                              >
                                {(f.options || []).map((o) => (
                                  <option key={o.value ?? o} value={o.value ?? o}>
                                    {o.label ?? o}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type={f.type || "text"}
                                className="gf-input"
                                value={row.meta[f.key] ?? ""}
                                disabled={isLocked}
                                onChange={(e) => updateMeta(row.id, { [f.key]: e.target.value })}
                                data-testid={`bulk-${f.key}-${row.id}`}
                              />
                            )}
                          </td>
                        ))}
                        <td className="font-mono text-xs uppercase">
                          {row.status === "pending" && <span className="text-zinc-500">pending</span>}
                          {row.status === "uploading" && <span className="text-blue-700">uploading…</span>}
                          {row.status === "done" && <span className="text-emerald-700">✓ done</span>}
                          {row.status === "error" && <span className="text-rose-700">✗ error</span>}
                        </td>
                        <td className="text-xs">
                          {row.status === "done" && <span className="text-emerald-700">{row.summary}</span>}
                          {row.status === "error" && <span className="text-rose-700">{row.error}</span>}
                        </td>
                        <td>
                          {!isLocked && (
                            <button
                              type="button"
                              className="text-xs text-zinc-400 hover:text-rose-600"
                              onClick={() => remove(row.id)}
                              data-testid={`bulk-remove-${row.id}`}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-zinc-200 flex justify-between items-center gap-2">
          <div className="text-xs text-zinc-500 font-mono">
            {busy ? "Uploads running — leaving the page is safe, files complete server-side."
                  : allDone ? "All files uploaded."
                  : "Tip: edit per-row metadata before clicking Upload all."}
          </div>
          <div className="flex gap-2">
            <button className="gf-btn gf-btn-outline" onClick={close} disabled={busy} data-testid="bulk-close">
              {allDone ? "Close" : "Cancel"}
            </button>
            {!allDone && (
              <button
                className="gf-btn gf-btn-primary"
                onClick={runQueue}
                disabled={busy || queue.length === 0}
                data-testid="bulk-upload-run"
              >
                {busy ? "Uploading…" : `Upload ${queue.filter((r) => r.status !== "done").length} file(s)`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
